import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import Logo from "icons/new-logo.svg";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    backgroundColor: "hsl(140 10% 95%)",
    borderRadius: "0.5rem",
    fontFamily: "'Segoe UI Regular', 'Work Sans', Roboto, Helvetica, Arial, sans-serif",
    "& fieldset": {
      borderColor: "hsl(140 8% 88%)",
    },
    "&:hover fieldset": {
      borderColor: "hsl(150 10% 46%)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "hsl(150 38% 17%)",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
};

const labelSx = {
  display: "block",
  fontSize: "12px",
  fontWeight: 500,
  color: "hsl(150 10% 46%)",
  mb: "0.5rem !important",
  fontFamily: "'Segoe UI Regular', 'Work Sans', Roboto, Helvetica, Arial, sans-serif",
};

function InternalAcceptInvitationPage() {
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const invitee = useSelector((state) => state.invitee);
  const invitationToken = useSelector((state) => state.invitation_token);

  const [values, setValues] = useState({ password: "", password_confirmation: "" });
  const [loading, setLoading] = useState(false);

  const updateValue = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!values.password || !values.password_confirmation) {
      enqueueSnackbar("Please fill in all required fields.", { variant: "error" });
      return;
    }

    if (values.password !== values.password_confirmation) {
      enqueueSnackbar("Passwords do not match.", { variant: "error" });
      return;
    }

    setLoading(true);
    sh.put("/internal/invitation", {
      authenticity_token: authenticityToken,
      password: values.password,
      password_confirmation: values.password_confirmation,
      invitation_token: invitationToken,
    })
      .then(() => {
        enqueueSnackbar("Password set successfully.", { variant: "success" });
        window.location.replace("/internal/dashboard");
      })
      .catch((err) => {
        const errors = err.response?.data?.errors;
        enqueueSnackbar(errors?.[0] || "Failed to accept invitation.", { variant: "error" });
        setLoading(false);
      });
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100dvh",
        backgroundColor: "hsl(140 10% 95%)",
        px: { xs: 2, sm: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: "28rem", mx: "auto", px: 0 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={Logo}
            alt="Squarehub"
            sx={{ height: 36 }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid hsl(140 8% 88%)",
            borderRadius: "0.75rem",
            p: { xs: 3, sm: 4 },
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              lineHeight: 1.4,
              fontWeight: 700,
              color: "hsl(150 30% 12%)",
              mb: 0.5,
              fontFamily: "'Segoe UI Bold', 'Work Sans', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            Welcome{invitee?.first_name ? `, ${invitee.first_name}` : ""}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "hsl(150 10% 46%)",
              mb: 3.5,
              fontFamily: "'Segoe UI Regular', 'Work Sans', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            Create your password to finish setting up your internal account.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography component="label" htmlFor="accept-password" sx={labelSx}>
                Password
                <Box component="span" sx={{ color: "hsl(0 84.2% 60.2%)", fontWeight: 600, ml: 0.5, fontSize: "11px" }}>*</Box>
              </Typography>
              <TextField
                id="accept-password"
                required
                name="password"
                type="password"
                placeholder="Enter your password"
                value={values.password}
                onChange={updateValue}
                fullWidth
                disabled={loading}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "hsl(150 10% 46%)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="accept-password-confirm" sx={labelSx}>
                Confirm password
                <Box component="span" sx={{ color: "hsl(0 84.2% 60.2%)", fontWeight: 600, ml: 0.5, fontSize: "11px" }}>*</Box>
              </Typography>
              <TextField
                id="accept-password-confirm"
                required
                name="password_confirmation"
                type="password"
                placeholder="Confirm your password"
                value={values.password_confirmation}
                onChange={updateValue}
                fullWidth
                disabled={loading}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "hsl(150 10% 46%)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  height: 44,
                  px: 3,
                  borderRadius: "0.5rem",
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: "'Segoe UI Semibold', 'Work Sans', Roboto, Helvetica, Arial, sans-serif",
                  backgroundColor: "hsl(150 38% 17%)",
                  boxShadow: "none",
                  color: "hsl(80 97% 55%)",
                  "&:hover": {
                    backgroundColor: "hsl(150 30% 22%)",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Saving..." : "Set password"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default InternalAcceptInvitationPage;
